/*
 * Driver Management Component
 *
 * Admin interface for managing garbage truck drivers:
 * - Create driver accounts (username + password login)
 * - Assign each driver to a garbage truck
 * - Enable/disable accounts and reset passwords
 * - View recent route sessions (who ran which route and when)
 *
 * Drivers log in at /driver and only see their assigned truck and its routes.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/config';

const emptyForm = {
  name: '',
  username: '',
  password: '',
  phone: '',
  assigned_vehicle_id: '',
  is_active: true
};

const DriverManagement = () => {
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const authHeaders = () => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` }
  });

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [driversRes, vehiclesRes, sessionsRes] = await Promise.all([
        axios.get(`${API}/admin/drivers/`, authHeaders()),
        axios.get(`${API}/vehicles/public`),
        axios.get(`${API}/admin/drivers/route-sessions`, authHeaders())
      ]);
      setDrivers(driversRes.data);
      setVehicles(vehiclesRes.data);
      setSessions(sessionsRes.data);
      setError('');
    } catch (err) {
      setError('Failed to load drivers');
    } finally {
      setLoading(false);
    }
  };

  const flashSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleCreate = () => {
    setEditingDriver(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const handleEdit = (driver) => {
    setEditingDriver(driver);
    setForm({
      name: driver.name,
      username: driver.username,
      password: '',
      phone: driver.phone || '',
      assigned_vehicle_id: driver.assigned_vehicle_id || '',
      is_active: driver.is_active
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.username.trim()) {
      setError('Name and username are required');
      return;
    }
    if (!editingDriver && form.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (editingDriver && form.password && form.password.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        username: form.username.trim(),
        phone: form.phone.trim() || null,
        assigned_vehicle_id: form.assigned_vehicle_id || null,
        is_active: form.is_active
      };
      if (form.password) payload.password = form.password;

      if (editingDriver) {
        await axios.put(`${API}/admin/drivers/${editingDriver.id}`, payload, authHeaders());
        flashSuccess('Driver updated successfully');
      } else {
        await axios.post(`${API}/admin/drivers/`, payload, authHeaders());
        flashSuccess('Driver created successfully');
      }
      setShowForm(false);
      setEditingDriver(null);
      setError('');
      loadAll();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save driver');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (driver) => {
    if (!window.confirm(`Delete driver "${driver.name}"? They will no longer be able to log in.`)) return;
    try {
      await axios.delete(`${API}/admin/drivers/${driver.id}`, authHeaders());
      flashSuccess('Driver deleted');
      loadAll();
    } catch (err) {
      setError('Failed to delete driver');
    }
  };

  const handleToggleActive = async (driver) => {
    try {
      await axios.put(`${API}/admin/drivers/${driver.id}`, { is_active: !driver.is_active }, authHeaders());
      flashSuccess(`Driver ${driver.is_active ? 'disabled' : 'enabled'}`);
      loadAll();
    } catch (err) {
      setError('Failed to update driver');
    }
  };

  const vehicleLabel = (v) =>
    `${v.license_plate} - ${`${v.make || ''} ${v.model || ''}`.trim() || 'Truck'}`;

  const formatDate = (d) => {
    if (!d) return '—';
    const date = new Date(d.endsWith && !d.endsWith('Z') && !d.includes('+') ? `${d}Z` : d);
    return date.toLocaleString('en-BZ', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">👷 Driver Management</h2>
          <p className="text-gray-600 mt-1">
            Create driver accounts and assign each driver to a garbage truck. Drivers log in at <span className="font-mono text-sm bg-gray-100 px-1 rounded">/driver</span>
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowHelp(!showHelp)}
            className={`px-4 py-2 rounded-md flex items-center ${
              showHelp ? 'bg-indigo-600 text-white' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
            }`}
          >
            <span className="mr-2">❓</span>
            How It Works
          </button>
          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
          >
            <span className="mr-2">+</span>
            Add Driver
          </button>
        </div>
      </div>

      {/* How It Works - Instructions Panel */}
      {showHelp && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-5 space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-indigo-900">❓ How Drivers Work</h3>
            <button onClick={() => setShowHelp(false)} className="text-indigo-500 hover:text-indigo-700">✕ Close</button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-indigo-900">
            <div className="bg-white rounded-lg p-4">
              <h4 className="font-semibold mb-2">🚀 Setting up a driver (step by step)</h4>
              <ol className="list-decimal ml-4 space-y-1">
                <li>Click <strong>+ Add Driver</strong> and enter their full name.</li>
                <li>Choose a <strong>username</strong> (short, no spaces — e.g., <span className="font-mono bg-gray-100 px-1 rounded">jgarcia</span>) and a <strong>password</strong> (at least 6 characters). Give both to the driver.</li>
                <li>Select their <strong>Assigned Garbage Truck</strong> — the driver will only ever see this one truck.</li>
                <li>Save. The driver can now log in at <span className="font-mono bg-gray-100 px-1 rounded">/driver</span> on any phone or computer.</li>
              </ol>
              <p className="mt-2 text-xs text-indigo-700">
                Driver accounts are separate from resident accounts and admin accounts — a driver login only works on the /driver page.
              </p>
            </div>

            <div className="space-y-4">
              <div className="bg-white rounded-lg p-4">
                <h4 className="font-semibold mb-2">📱 What the driver sees after logging in</h4>
                <ul className="list-disc ml-4 space-y-1">
                  <li>Their assigned truck and whether its GPS is online.</li>
                  <li><strong>Today's Routes</strong> — routes assigned to their truck whose Collection Days include today (set both in the <strong>Garbage Truck Routes</strong> tab).</li>
                  <li>Pressing <strong>▶ Start Route</strong> opens a live map showing the route, its stops, and the truck's real position — with an ON ROUTE / OFF ROUTE banner and a progress bar, so they can confirm they are following the route.</li>
                  <li>Every started route appears below in <strong>Recent Route Sessions</strong>, so you can check who ran which route and when.</li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-4">
                <h4 className="font-semibold mb-2">🔧 Troubleshooting</h4>
                <ul className="list-disc ml-4 space-y-1">
                  <li><strong>Driver sees "No routes"</strong> → the truck isn't assigned to any route. Edit the route in Garbage Truck Routes and select the truck under "Assigned Vehicles".</li>
                  <li><strong>Route not under "Today's Routes"</strong> → today isn't in the route's Collection Days.</li>
                  <li><strong>Forgotten password</strong> → edit the driver and type a new password (leave blank to keep the old one).</li>
                  <li><strong>Driver left the job</strong> → use 🚫 to disable login instantly, or 🗑️ to delete the account.</li>
                  <li><strong>Truck shows "GPS Offline"</strong> → the GPS device hasn't reported recently; check the device in the Garbage Trucks tab.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
          <button onClick={() => setError('')} className="float-right text-red-700 hover:text-red-900">✕</button>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Driver Form */}
      {showForm && (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {editingDriver ? `Edit Driver: ${editingDriver.name}` : 'New Driver'}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Juan Garcia"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username *</label>
              <input
                type="text"
                value={form.username}
                onChange={(e) => setForm(prev => ({ ...prev, username: e.target.value.toLowerCase().replace(/\s/g, '') }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., jgarcia"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {editingDriver ? 'New Password (leave blank to keep current)' : 'Password *'}
              </label>
              <input
                type="text"
                value={form.password}
                onChange={(e) => setForm(prev => ({ ...prev, password: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Minimum 6 characters"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="text"
                value={form.phone}
                onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 610-1234"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Garbage Truck</label>
              <select
                value={form.assigned_vehicle_id}
                onChange={(e) => setForm(prev => ({ ...prev, assigned_vehicle_id: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">— No truck assigned —</option>
                {vehicles.map((v) => (
                  <option key={v.id} value={v.id}>{vehicleLabel(v)}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">The driver will only see this truck when they log in</p>
            </div>
            <div className="flex items-center pt-6">
              <input
                type="checkbox"
                id="driver_is_active"
                checked={form.is_active}
                onChange={(e) => setForm(prev => ({ ...prev, is_active: e.target.checked }))}
                className="mr-2 h-4 w-4 text-blue-600"
              />
              <label htmlFor="driver_is_active" className="text-sm text-gray-700">
                Active (driver can log in)
              </label>
            </div>
          </div>

          <div className="flex space-x-2 pt-4 border-t border-gray-200">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : (editingDriver ? '💾 Update Driver' : '💾 Create Driver')}
            </button>
            <button
              onClick={() => { setShowForm(false); setEditingDriver(null); }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Drivers List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Drivers ({drivers.length})</h3>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-600">Loading drivers...</div>
        ) : drivers.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-gray-400 text-4xl mb-4">👷</div>
            <p className="text-gray-600">No drivers yet</p>
            <button
              onClick={handleCreate}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Add First Driver
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {drivers.map((driver) => (
              <div key={driver.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h4 className="font-medium text-gray-900">{driver.name}</h4>
                      {driver.is_active ? (
                        <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">Active</span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full">Disabled</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Username: <span className="font-mono">{driver.username}</span>
                      {driver.phone && <> • 📞 {driver.phone}</>}
                    </p>
                    <p className="text-xs mt-1">
                      {driver.vehicle_license_plate ? (
                        <span className="text-blue-700">
                          🚛 {driver.vehicle_license_plate}
                          {driver.vehicle_description && ` (${driver.vehicle_description})`}
                        </span>
                      ) : (
                        <span className="text-yellow-600">⚠️ No truck assigned</span>
                      )}
                    </p>
                  </div>
                  <div className="flex space-x-1 ml-2">
                    <button
                      onClick={() => handleToggleActive(driver)}
                      className={`p-1 rounded text-xs ${
                        driver.is_active
                          ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                      title={driver.is_active ? 'Disable login' : 'Enable login'}
                    >
                      {driver.is_active ? '🚫' : '✅'}
                    </button>
                    <button
                      onClick={() => handleEdit(driver)}
                      className="p-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-xs"
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDelete(driver)}
                      className="p-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-xs"
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Route Sessions */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Recent Route Sessions</h3>
          <p className="text-xs text-gray-500 mt-1">Routes started by drivers from the driver app</p>
        </div>
        {sessions.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">No route sessions yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Route</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Started</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ended</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sessions.map((s) => {
                  const driver = drivers.find(d => d.id === s.driver_id);
                  return (
                    <tr key={s.id}>
                      <td className="px-4 py-2">{driver ? driver.name : s.driver_id.substring(0, 8)}</td>
                      <td className="px-4 py-2">{s.route_name || s.route_id.substring(0, 8)}</td>
                      <td className="px-4 py-2 text-gray-600">{formatDate(s.started_at)}</td>
                      <td className="px-4 py-2 text-gray-600">{formatDate(s.ended_at)}</td>
                      <td className="px-4 py-2">
                        {s.status === 'active' ? (
                          <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">🟢 Active</span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">Completed</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverManagement;
